import PropTypes from "prop-types";
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Sparkles, ArrowRight, MessageCircle, Linkedin, ShieldCheck, Lock, Server, Cloud, CheckCircle2, ChevronDown, Play, Calendar, Copy, KeyRound, Settings } from 'lucide-react';
import { useState } from "react";
import { toast } from "sonner";
import { NamedTestimonials } from "@/components/marketing/NamedTestimonials";
import { DemoBookingSheet } from "@/components/marketing/DemoBookingSheet";
import { DemoVideoUnavailableDialog } from "@/components/marketing/DemoVideoUnavailableDialog";
import { PublicSignupCta } from "@/components/auth/PublicSignupCta";
import { Logo } from "@/components/common/Logo";

// Phase 9 — Public marketing landings: /marketing/saas /marketing/hardware /marketing/cloud

const TRUSTED_LOGOS = ["Linear", "Stripe", "Notion", "Vercel", "Ramp", "Datadog"];
const COMPLIANCE = [{ Icon: ShieldCheck, label: "SOC 2 Type II" }, { Icon: Lock, label: "GDPR" }, { Icon: Server, label: "AES-256" }];

function TopNav({ ctaLabel, ctaTo = "/onboarding/welcome" }) {
  return (
    <nav className="sticky top-0 z-20 bg-white/80 backdrop-blur border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center gap-2 sm:gap-4 min-w-0">
        <Link to="/" className="shrink-0" data-testid="nav-logo">
          <Logo size={22} />
        </Link>
        <div className="ml-auto flex items-center gap-0.5 sm:gap-1 min-w-0">
          <Link to="/marketing/saas" className="hidden sm:inline text-[12.5px] font-medium text-slate-600 hover:text-slate-900 px-2.5 py-1.5">Pricing</Link>
          <Link to="/login" className="text-[12.5px] font-medium text-slate-600 hover:text-slate-900 px-2 sm:px-2.5 py-1.5">Sign in</Link>
          <Link to={ctaTo}><Button size="sm" className="h-9 sm:h-8 text-[12px] ml-1 sm:ml-2">{ctaLabel} <ArrowRight size={12} /></Button></Link>
        </div>
      </div>
    </nav>
  );
}
TopNav.propTypes = { ctaLabel: PropTypes.string.isRequired, ctaTo: PropTypes.string };

function Footer() {
  return (
    <footer className="border-t border-slate-200 bg-slate-50/40 mt-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-[12px] text-slate-600">
        <div>
          <Logo size={18} />
          <div className="mt-2 text-[11px] text-slate-500">Your personal AI SDR · Powered by OpenClaw.</div>
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Product</div>
          <Link to="/marketing/saas"     className="block py-0.5 hover:text-slate-900">Private SaaS</Link>
          <Link to="/marketing/cloud"    className="block py-0.5 hover:text-slate-900">Self-hosted Cloud</Link>
          <Link to="/login"               className="block py-0.5 hover:text-slate-900">Sign in</Link>
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Trust</div>
          <Link to="/privacy" className="block py-0.5 hover:text-slate-900">Privacy</Link>
          <Link to="/terms" className="block py-0.5 hover:text-slate-900">Terms</Link>
          <Link to="/security" className="block py-0.5 hover:text-slate-900">Security</Link>
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold mb-1.5">Contact</div>
          <div>hello@sellix.ai</div>
          <div>San Francisco · London · Bengaluru</div>
        </div>
      </div>
    </footer>
  );
}

function Faq({ items }) {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-200">
      {Array.from(items).map((it, i) => (
        <button key={it.q} onClick={() => setOpenIdx(openIdx === i ? -1 : i)} className="w-full text-left px-5 py-4 hover:bg-slate-50/50 transition-colors" data-testid={`faq-${i}`}>
          <div className="flex items-center justify-between gap-3">
            <span className="text-[13.5px] font-semibold text-slate-900">{it.q}</span>
            <ChevronDown size={14} className={cn("text-slate-400 transition-transform shrink-0", openIdx === i && "rotate-180")} />
          </div>
          {openIdx === i && <div className="mt-2 text-[12.5px] text-slate-600 leading-relaxed">{it.a}</div>}
        </button>
      ))}
    </div>
  );
}
Faq.propTypes = { items: PropTypes.arrayOf(PropTypes.shape({ q: PropTypes.string.isRequired, a: PropTypes.node.isRequired })).isRequired };

function MarketingLayout({ variant, hero, ctaLabel, pricing, whyReasons, faqs, intent = "saas", extraSections }) {
  const signupHref = intent === "saas" ? "/signup" : `/signup?intent=${intent}`;
  const [demoOpen, setDemoOpen] = useState(false);
  const [videoUnavailableOpen, setVideoUnavailableOpen] = useState(false);
  return (
    <div className="bg-white">
      <TopNav ctaLabel={ctaLabel} />

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pt-10 sm:pt-16 pb-10 sm:pb-12 grid md:grid-cols-2 gap-8 sm:gap-10 items-center min-w-0">
        <div>
          <div className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 ring-1 ring-indigo-200 px-2.5 py-0.5 text-[10.5px] font-semibold text-indigo-700">
            <Sparkles size={10} /> {variant}
          </div>
          <h1 className="mt-4 text-[32px] sm:text-[44px] md:text-[52px] font-semibold tracking-tight text-slate-900 leading-[1.05]" data-testid="hero-headline">{hero.headline}</h1>
          <p className="mt-4 text-[15px] text-slate-600 leading-relaxed max-w-lg">{hero.sub}</p>
          <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-wrap">
            <PublicSignupCta
              to={signupHref}
              buttonProps={{ size: "lg", className: "h-11 text-[13px] px-5 w-full sm:w-auto", "data-testid": "hero-primary-cta" }}
            >
              {ctaLabel} <ArrowRight size={13} />
            </PublicSignupCta>
            <Button variant="outline" size="lg" className="h-11 text-[13px] px-4 w-full sm:w-auto" data-testid="hero-demo-cta" onClick={() => setVideoUnavailableOpen(true)}><Play size={12} /> Watch a 2-min demo</Button>
          </div>
          <div className="mt-5 inline-flex items-center gap-2 text-[11px] text-slate-500">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> No credit card · cancel anytime
          </div>
        </div>
        <div className="relative">
          <div className="rounded-3xl bg-gradient-to-br from-indigo-100/70 via-violet-100/40 to-emerald-50/40 aspect-square ring-1 ring-slate-200/60 shadow-sm overflow-hidden flex items-center justify-center">
            <div className="rounded-2xl bg-white shadow-lg ring-1 ring-slate-200 w-[78%] aspect-[5/4] p-4">
              <div className="text-[10px] uppercase tracking-wider text-indigo-600 font-semibold">Maya · Overnight</div>
              <div className="mt-1 text-[14px] font-semibold text-slate-900 leading-snug">Good morning, Aman. While you slept, I replied to <span className="text-indigo-700">7</span> WhatsApp messages and booked <span className="text-indigo-700">2</span> demos.</div>
              <div className="mt-3 space-y-1.5">
                {["Replied to Sarah (Linear) — pricing", "Booked Helen (Stripe) for Thursday 2pm", "Flagged 1 hot signal from r/SaaS"].map((l) => (
                  <div key={l} className="flex items-center gap-2 text-[11.5px] text-slate-700"><CheckCircle2 size={11} className="text-emerald-500 shrink-0" /><span>{l}</span></div>
                ))}
              </div>
              <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500">Powered by OpenClaw · v2.4.1</div>
            </div>
          </div>
        </div>
      </section>

      {/* What is Sellyx */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold text-center">What is Sellyx?</div>
        <h2 className="mt-1 text-[22px] sm:text-[28px] font-semibold tracking-tight text-slate-900 text-center">Your personal AI SDR — on your channels, in your voice.</h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
          {[
            { Icon: MessageCircle, title: "Works as you on your channels", body: "WhatsApp, LinkedIn, Gmail, Telegram, SMS — your accounts, not ours. No platform APIs." },
            { Icon: Linkedin,      title: "Replies in seconds, 24/7",       body: "Maya keeps up with your inbox while you sleep. Drafts ready when you wake up." },
            { Icon: ShieldCheck,   title: "Your data, your instance",         body: "Sessions live on your Sellyx. We can&rsquo;t see them — your AI provider can&rsquo;t either." },
          ].map((p) => (
            <div key={p.title} className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="h-9 w-9 rounded-lg bg-indigo-50 text-indigo-700 flex items-center justify-center"><p.Icon size={16} /></div>
              <div className="mt-3 text-[14px] font-semibold text-slate-900">{p.title}</div>
              <div className="text-[12px] text-slate-600 mt-1 leading-relaxed" dangerouslySetInnerHTML={{ __html: p.body }} />
            </div>
          ))}
        </div>
      </section>

      {/* Why this variant */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-12 bg-slate-50/40 rounded-3xl">
        <div className="text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold text-center">Why {variant}</div>
        <h2 className="mt-1 text-[26px] font-semibold tracking-tight text-slate-900 text-center max-w-xl mx-auto">{whyReasons.heading}</h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
          {Array.from(whyReasons.points).map((p) => (
            <div key={p.title} className="rounded-2xl bg-white border border-slate-200 p-5">
              <div className="h-9 w-9 rounded-lg bg-emerald-50 text-emerald-700 flex items-center justify-center"><p.Icon size={16} /></div>
              <div className="mt-3 text-[14px] font-semibold text-slate-900">{p.title}</div>
              <div className="text-[12px] text-slate-600 mt-1 leading-relaxed">{p.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-14 text-center">
        <div className="text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold">Pricing</div>
        <h2 className="mt-1 text-[22px] sm:text-[28px] font-semibold tracking-tight text-slate-900">{pricing.title}</h2>
        <div className="mt-6 rounded-3xl border border-indigo-200 ring-2 ring-indigo-100 bg-gradient-to-br from-white via-indigo-50/30 to-violet-50/20 p-5 sm:p-7 text-left">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-[13px] font-semibold text-slate-700">{pricing.tier}</div>
              <div className="mt-1 text-[28px] sm:text-[36px] font-semibold tracking-tight text-indigo-700 tabular-nums leading-none">{pricing.price}</div>
              <div className="text-[12px] text-slate-500 mt-1">{pricing.cadence}</div>
            </div>
            <PublicSignupCta
              to={signupHref}
              buttonProps={{ size: "lg", className: "h-11 text-[13px] px-5 w-full sm:w-auto", "data-testid": "pricing-cta" }}
            >
              {ctaLabel} <ArrowRight size={13} />
            </PublicSignupCta>
          </div>
          <ul className="mt-5 grid md:grid-cols-2 gap-2 text-[12.5px] text-slate-700">
            {Array.from(pricing.includes).map((x) => <li key={x} className="inline-flex items-center gap-1.5"><CheckCircle2 size={11} className="text-emerald-600" /> {x}</li>)}
          </ul>
        </div>
      </section>

      {/* Trust strip */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8 border-y border-slate-200/60">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-1.5 flex-wrap">
            {COMPLIANCE.map((c) => (
              <span key={c.label} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 ring-1 ring-emerald-200 px-2 py-0.5 text-[10.5px] font-semibold text-emerald-700"><c.Icon size={10} /> {c.label}</span>
            ))}
          </div>
          <div className="text-[11px] text-slate-500">Trusted by founders at <span className="text-slate-800 font-semibold">{TRUSTED_LOGOS.join(" · ")}</span></div>
          <div className="text-[10.5px] text-slate-400">Powered by <span className="font-mono text-indigo-700">OpenClaw</span></div>
        </div>
      </section>

      {/* Extra cloud-specific sections injected here */}
      {extraSections}

      {/* Named testimonials */}
      <NamedTestimonials eyebrow="Loved by founders" />

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <h2 className="text-[22px] font-semibold tracking-tight text-slate-900 text-center mb-5">Common questions</h2>
        <Faq items={faqs} />
      </section>

      {/* Bottom CTA */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-14 text-center">
        <h2 className="text-[22px] sm:text-[26px] font-semibold tracking-tight text-slate-900">{hero.bottomCta || "Get your AI SDR live in 5 minutes."}</h2>
        <PublicSignupCta
          to={signupHref}
          buttonProps={{ size: "lg", className: "mt-4 h-11 text-[13px] px-5 w-full sm:w-auto", "data-testid": "bottom-cta" }}
        >
          {ctaLabel} <ArrowRight size={13} />
        </PublicSignupCta>
        <div className="mt-4">
          <button type="button" onClick={() => setDemoOpen(true)} className="inline-flex items-center gap-1.5 text-[12.5px] text-slate-500 hover:text-slate-800 underline-offset-2 hover:underline" data-testid="marketing-book-demo">
            <Calendar size={12} /> Or book a 30-min call with our team
          </button>
        </div>
      </section>

      <Footer />
      <DemoBookingSheet open={demoOpen} onOpenChange={setDemoOpen} />
      <DemoVideoUnavailableDialog open={videoUnavailableOpen} onOpenChange={setVideoUnavailableOpen} onBookDemo={() => setDemoOpen(true)} />
    </div>
  );
}
MarketingLayout.propTypes = {
  variant: PropTypes.string.isRequired,
  hero: PropTypes.shape({ headline: PropTypes.node.isRequired, sub: PropTypes.node.isRequired, bottomCta: PropTypes.node }).isRequired,
  ctaLabel: PropTypes.string.isRequired,
  pricing: PropTypes.shape({ title: PropTypes.node.isRequired, tier: PropTypes.node.isRequired, price: PropTypes.node.isRequired, cadence: PropTypes.node.isRequired, includes: PropTypes.arrayOf(PropTypes.node).isRequired }).isRequired,
  whyReasons: PropTypes.shape({ heading: PropTypes.node.isRequired, points: PropTypes.arrayOf(PropTypes.shape({ Icon: PropTypes.elementType.isRequired, title: PropTypes.node.isRequired, body: PropTypes.node.isRequired })).isRequired }).isRequired,
  faqs: PropTypes.arrayOf(PropTypes.shape({ q: PropTypes.string.isRequired, a: PropTypes.node.isRequired })).isRequired,
  intent: PropTypes.string,
  extraSections: PropTypes.node,
};

function CloudHowItDeploys() {
  const tf = `module "sellix" {
  source  = "sellix/sellix/aws"
  version = "1.4.2"
  region  = "us-west-2"
  kms_key_arn = var.kms_key_arn
  instance_type = "c6i.2xlarge"
}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(tf); } catch (_) {}
    toast.success("Copied");
  };
  const steps = [
    { Icon: Cloud,    title: "Connect AWS or GCP",    body: "Point our Terraform module at your account. We support AWS (primary), GCP, and Azure." },
    { Icon: KeyRound, title: "Provide an IAM role",   body: "We use scoped credentials — VPC + KMS only. Nothing global." },
    { Icon: Lock,     title: "Bring your own KMS",    body: "Sellyx encrypts everything with your KMS key. We never see plaintext." },
    { Icon: Settings, title: "Run Terraform",         body: "One module, one apply. Maya is live in ~12 minutes." },
  ];
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14" data-testid="cloud-how-it-deploys">
      <div className="text-[10.5px] uppercase tracking-wider text-indigo-700 font-semibold text-center">How it deploys</div>
      <h2 className="mt-1 text-[22px] sm:text-[26px] md:text-[30px] font-semibold tracking-tight text-slate-900 text-center px-1">How Sellyx deploys to your cloud</h2>
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
        {steps.map((s, i) => (
          <div key={s.title} className="rounded-2xl border border-slate-200 bg-white p-5 relative" data-testid={`cloud-step-${i}`}>
            <span className="inline-flex h-6 w-6 rounded-full bg-indigo-600 text-white text-[11px] font-semibold items-center justify-center">{i + 1}</span>
            <s.Icon size={16} className="text-indigo-600 mt-2" />
            <div className="mt-2 text-[13.5px] font-semibold text-slate-900">{s.title}</div>
            <div className="text-[12px] text-slate-600 mt-1 leading-relaxed">{s.body}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-900 p-4 max-w-3xl mx-auto" data-testid="terraform-block">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10.5px] uppercase tracking-wider text-slate-400 font-semibold font-mono">main.tf</div>
          <button type="button" onClick={copy} className="inline-flex items-center gap-1 rounded-md bg-slate-800 hover:bg-slate-700 px-2.5 py-1 text-[11px] text-slate-300 ring-1 ring-slate-700" data-testid="terraform-copy"><Copy size={11} /> Copy</button>
        </div>
        <pre className="text-[12px] leading-relaxed text-emerald-300 font-mono overflow-x-auto whitespace-pre">{tf}</pre>
      </div>
    </section>
  );
}

export function MarketingSaaS() {
  return (
    <MarketingLayout
      variant="Private SaaS"
      ctaLabel="Start your free trial"
      hero={{
        headline: "Your personal AI SDR. Live in 5 minutes.",
        sub: "Sellyx runs as a private single-tenant instance. Connect your WhatsApp, LinkedIn, and Gmail with one scan. No APIs, no setup, no missed leads.",
        bottomCta: "Get your AI SDR live in 5 minutes.",
      }}
      whyReasons={{
        heading: "The fastest way to get Maya into your day.",
        points: [
          { Icon: Sparkles,     title: "Zero setup",            body: "One QR scan and Maya is live on your WhatsApp. No platform forms, no API keys." },
          { Icon: ShieldCheck,  title: "Single-tenant",         body: "Your instance is yours alone. We never co-mingle your conversations with another founder's." },
          { Icon: Server,       title: "99.97% uptime",          body: "Sellyx-managed infrastructure with rolling deploys and zero scheduled maintenance windows." },
        ],
      }}
      pricing={{
        title: "One plan. Everything Maya needs.",
        tier: "Private SaaS · Founder + Team",
        price: "$999/mo",
        cadence: "Billed monthly · cancel anytime",
        includes: ["All channels (WhatsApp, LinkedIn, Gmail, Telegram, SMS, Web)", "Up to 5 collaborators", "99.97% uptime SLA", "Private single-tenant instance", "Unlimited Maya conversations", "Lifetime model upgrades"],
      }}
      faqs={[
        { q: "How does Maya work on my WhatsApp?", a: "We pair your phone via WhatsApp's official multi-device protocol — the same one WhatsApp Web uses. Maya runs as a linked device on your own number." },
        { q: "Is Sellyx multi-tenant?",            a: "No. Each Sellyx customer gets a dedicated single-tenant container. Your data is physically isolated from other customers — including in the AI model context window." },
        { q: "Can I cancel anytime?",              a: "Yes. Month-to-month. We export everything Maya has learned about your voice + conversations on the way out." },
        { q: "What happens if I get a ban?",       a: "Maya pre-emptively keeps you under platform limits + warms up new accounts. We've had zero permanent bans across 2,000+ paired channels." },
        { q: "Where does my data live?",           a: "US-West (Oregon) by default. EU-West and APAC-South available on request. Encrypted at rest with AES-256 and in transit with TLS 1.3." },
      ]}
    />
  );
}

export function MarketingHardware() {
  // V2 unlock: Hardware track removed. Component preserved as redirect-only stub to avoid
  // accidental imports breaking the build; route in App.js now redirects /marketing/hardware → /marketing/saas.
  return null;
}

export function MarketingCloud() {
  return (
    <MarketingLayout
      variant="Self-hosted Cloud"
      ctaLabel="Self-host now"
      intent="cloud"
      hero={{
        headline: "Your AI SDR. Your cloud. Your keys.",
        sub: "Run Sellyx on your own AWS or GCP. Bring your own KMS. Built for founders with strict data-residency or compliance needs.",
        bottomCta: "Deploy Maya into your VPC.",
      }}
      whyReasons={{
        heading: "Maximum control. Same Maya.",
        points: [
          { Icon: Cloud,       title: "Your AWS / GCP",       body: "Deploy with Terraform into your existing account. Your VPC, your subnets, your IAM." },
          { Icon: Lock,        title: "Bring your own keys",  body: "Encrypt everything with your KMS — including the model context window. We can't decrypt your data." },
          { Icon: Server,      title: "Custom audit logs",     body: "Ship every Maya action into your Datadog / Splunk / OpenSearch with one flag." },
        ],
      }}
      pricing={{
        title: "Same Maya. Lives in your cloud.",
        tier: "Self-hosted Cloud · Founder + Team",
        price: "$1,499/mo",
        cadence: "Software license · your infra costs apply",
        includes: ["Deploy into AWS / GCP / Azure", "Bring your own KMS", "Your VPC + subnets", "Custom audit log export", "Up to 10 collaborators", "Priority support"],
      }}
      faqs={[
        { q: "Which clouds do you support?",          a: "AWS (primary), GCP, and Azure (in preview). Terraform modules ship for all three. EKS / GKE / AKS for the runtime." },
        { q: "How does BYOK work?",                    a: "You provide a KMS key alias. Sellyx uses it for all encryption — data at rest, model context, OpenClaw session pool. We never see plaintext." },
        { q: "Can I run Maya in a regulated region?", a: "Yes — GovCloud, AWS China (Beijing/Ningxia), and EU-residency are all supported. Pricing is the same." },
        { q: "How are updates delivered?",            a: "We publish signed container images. You pull them into your registry on your own schedule. Most customers run rolling deploys weekly." },
        { q: "What about cost?",                       a: "Software is $1,499/mo. Your AWS bill for a 5-person team is typically $200-400/mo on top. Most customers see total cost ~$1,800/mo." },
      ]}
      extraSections={<CloudHowItDeploys />}
    />
  );
}
